import { useFormContext, useFieldArray, useWatch, type FieldValues } from 'react-hook-form'
import { Plus, Trash2 } from 'lucide-react'
import { Card, CardHeader, CardTitle } from '@/shared/components/ui/card'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/components/ui/form'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { LocationFields } from '@/shared/components/location-fields/LocationFields'
import { LocationMapField } from '@/shared/components/location-map/LocationMapField'
import { emptyAddressEditItem, type AddressEditItem } from '@/shared/schemas/addressEditSchema'

interface AddressFieldsEditorProps {
  /** Path del array en el form. Default `'addresses'`. */
  name?: string
  /** Solo-lectura: deshabilita inputs y los botones de agregar/quitar. */
  disabled?: boolean
  title?: string
  description?: string
}

/**
 * Editor de direcciones reutilizable (alta/edición/baja por fila) enlazado al
 * form vía contexto. Encapsula el `useFieldArray` + el marcado `_deleted` para
 * que negocio y organización lo monten sin duplicar la lógica.
 *
 * - Fila existente borrada → se marca `_deleted` (el consumidor la envía como
 *   `{ id, delete: true }`); fila nueva borrada → se quita del array.
 * - Con `disabled` sirve de preview solo-lectura (p. ej. organización mientras
 *   el backend aún no acepta direcciones en la mutación de update).
 */
export function AddressFieldsEditor({
  name = 'addresses',
  disabled,
  title = 'Direcciones',
  description,
}: AddressFieldsEditorProps) {
  const { control, getValues, setValue } = useFormContext<FieldValues>()
  const { fields, append, remove } = useFieldArray({ control, name, keyName: 'fieldId' })

  // useWatch (no form.watch) → menos re-renders y sin warning de React Compiler.
  const watched = useWatch({ control, name }) as (AddressEditItem | undefined)[] | undefined

  const addRow = () => append(emptyAddressEditItem())

  const removeRow = (index: number) => {
    const row = getValues(`${name}.${index}`) as AddressEditItem | undefined
    if (row?.id) {
      // Existente → marcar para borrar (se envía { id, delete: true }).
      setValue(`${name}.${index}._deleted`, true, { shouldDirty: true, shouldValidate: true })
    } else {
      // Nueva sin guardar → quitar de la lista.
      remove(index)
    }
  }

  const visibleCount = (watched ?? []).filter((a) => !a?._deleted).length

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <Button type="button" variant="outline" size="sm" onClick={addRow} disabled={disabled}>
          <Plus className="h-4 w-4" />
          Agregar dirección
        </Button>
      </CardHeader>

      {description && <p className="mb-4 text-xs text-ink-muted">{description}</p>}

      <div className="space-y-5">
        {visibleCount === 0 && (
          <p className="text-sm text-ink-muted">No hay direcciones. Agrega al menos una.</p>
        )}
        {fields.map((field, index) => {
          if (watched?.[index]?._deleted) return null
          return (
            <div key={field.fieldId} className="rounded-card border border-line p-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">
                  Dirección {index + 1}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeRow(index)}
                  disabled={disabled}
                >
                  <Trash2 className="h-4 w-4" />
                  Eliminar
                </Button>
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <FormField
                    control={control}
                    name={`${name}.${index}.street1`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel required>Dirección</FormLabel>
                        <FormControl>
                          <Input placeholder="Ej. Calle 23 #456" disabled={disabled} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={control}
                  name={`${name}.${index}.city`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>Ciudad</FormLabel>
                      <FormControl>
                        <Input placeholder="Ej. La Habana" disabled={disabled} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <LocationFields name={`${name}.${index}`} disabled={disabled} />
                <div className="sm:col-span-2">
                  {/* Rótulo de sección: `Label` plano y NO `FormLabel`, que
                      llama `useFormField()` y requiere un `<FormField>`
                      envolvente (el mapa escribe en dos campos vía setValue,
                      no es un único field). */}
                  <Label>Ubicación en el mapa</Label>
                  <div className="mt-1.5">
                    <LocationMapField
                      readOnly={disabled}
                      latitude={watched?.[index]?.coordinateLatitude}
                      longitude={watched?.[index]?.coordinateLongitude}
                      onChange={(lat, lng) => {
                        setValue(`${name}.${index}.coordinateLatitude`, lat, {
                          shouldDirty: true,
                        })
                        setValue(`${name}.${index}.coordinateLongitude`, lng, {
                          shouldDirty: true,
                        })
                      }}
                    />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <FormField
                    control={control}
                    name={`${name}.${index}.isMain`}
                    render={({ field }) => (
                      <FormItem>
                        <label className="flex cursor-pointer items-center gap-2 text-sm text-ink">
                          <Checkbox
                            checked={!!field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                            disabled={disabled}
                          />
                          Dirección principal
                        </label>
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </Card>
  )
}
